'use client';

import type { IEnglishIqMilestone } from '@repo/sdk';
import type { EnglishIqMessagesType } from '../english-iq-messages-type';
import { useEnglishIqStageFocus } from '../hooks/wizard/use-english-iq-stage-focus';

interface IEnglishIqMilestoneProps {
  isBusy: boolean;
  messages: EnglishIqMessagesType['milestone'];
  milestone: IEnglishIqMilestone;
  onContinue: () => void;
  onFinish: () => void;
}

export function EnglishIqMilestone({
  isBusy,
  messages,
  milestone,
  onContinue,
  onFinish,
}: IEnglishIqMilestoneProps): React.JSX.Element {
  const headingRef = useEnglishIqStageFocus<HTMLHeadingElement>();
  return (
    <div className="mx-auto max-w-xl py-2 text-center sm:py-4">
      <div className="mx-auto flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-cyan-500 text-3xl text-white shadow-xl shadow-violet-500/20">
        ✓
      </div>
      <p className="mt-8 text-sm font-semibold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">
        {messages.eyebrow}
      </p>
      <h2
        className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
        ref={headingRef}
        tabIndex={-1}
      >
        {messages.title}
      </h2>
      <p className="mx-auto mt-4 max-w-lg text-base leading-7 text-muted-foreground">
        {messages.body}
      </p>
      <div className="mx-auto mt-7 max-w-sm rounded-2xl border border-foreground/10 bg-card p-4">
        <div
          aria-label={messages.confidence.replace(
            '{confidence}',
            String(Math.round(milestone.confidence)),
          )}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(milestone.confidence)}
          className="h-2 overflow-hidden rounded-full bg-foreground/10"
          role="progressbar"
        >
          <div
            className="h-full rounded-full bg-emerald-500"
            style={{ width: `${Math.min(100, milestone.confidence)}%` }}
          />
        </div>
        <p className="mt-3 text-sm font-medium text-foreground">
          {messages.confidence.replace('{confidence}', String(Math.round(milestone.confidence)))}
        </p>
      </div>
      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
        <button
          type="button"
          disabled={isBusy}
          onClick={onFinish}
          className="min-h-12 rounded-full bg-foreground px-7 py-3.5 font-semibold text-background transition hover:opacity-85 disabled:opacity-60 motion-reduce:transition-none"
        >
          {messages.finish}
        </button>
        {milestone.canContinue ? (
          <button
            type="button"
            disabled={isBusy}
            onClick={onContinue}
            className="min-h-12 rounded-full border border-foreground/15 bg-card px-7 py-3.5 font-semibold text-foreground transition hover:border-foreground/30 disabled:opacity-60 motion-reduce:transition-none"
          >
            {messages.continue}
          </button>
        ) : null}
      </div>
    </div>
  );
}
